/**
 * SecurityPanel - Workspace 2FA Policy Settings & Compliance Dashboard
 *
 * sl-qsjm.5: Allows workspace Owner/Admin to:
 * - Require TOTP for all members
 * - Allow Email-OTP as alternative
 * - View per-member 2FA compliance status
 */
import { Loading, notify, Switch } from '@affine/component';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { GraphQLService } from '@affine/core/modules/cloud/services/graphql';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  isBackendUnavailableError,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import {
  updateWorkspacePolicyMutation,
  workspaceMemberComplianceQuery,
  workspacePolicyQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { useSettingsOfflineIncompleteLabel } from '../../shared/settings-offline-status';
import * as styles from './styles.css';

interface WorkspacePolicy {
  requireTOTP: boolean;
  allowEmailOTP: boolean;
  gracePeriodDays: number;
  enforcedAt: string | null;
  gracePeriodActive: boolean;
  gracePeriodRemainingDays: number | null;
}

/** Fields accepted by the updateWorkspacePolicy mutation */
interface PolicyUpdateInput {
  requireTOTP?: boolean;
  allowEmailOTP?: boolean;
  gracePeriodDays?: number;
}

interface MemberCompliance {
  userId: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  hasTOTP: boolean;
  hasEmailOTP: boolean;
  role: string;
}

export const SecurityPanel = () => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceId = workspace.id;
  const permissionService = useService(WorkspacePermissionService).permission;
  const graphqlService = useService(GraphQLService);

  const isOwner = useLiveData(permissionService.isOwner$);
  const isAdmin = useLiveData(permissionService.isAdmin$);
  const canEdit = Boolean(isOwner || isAdmin);

  const [policy, setPolicy] = useState<WorkspacePolicy>({
    requireTOTP: false,
    allowEmailOTP: true,
    gracePeriodDays: 7,
    enforcedAt: null,
    gracePeriodActive: false,
    gracePeriodRemainingDays: null,
  });
  const [members, setMembers] = useState<MemberCompliance[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown | null>(null);
  const [updating, setUpdating] = useState(false);

  // Fetch policy and compliance data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    if (!backendAvailable) {
      setLoading(false);
      return;
    }
    try {
      const [policyRes, complianceRes] = await Promise.all([
        graphqlService.gql({
          query: workspacePolicyQuery,
          variables: { workspaceId },
        }),
        canEdit
          ? graphqlService.gql({
              query: workspaceMemberComplianceQuery,
              variables: { workspaceId },
            })
          : Promise.resolve(null),
      ]);

      const p = policyRes.workspacePolicy;
      setPolicy({
        requireTOTP: p.requireTOTP,
        allowEmailOTP: p.allowEmailOTP,
        gracePeriodDays: p.gracePeriodDays,
        enforcedAt: p.enforcedAt ?? null,
        gracePeriodActive: p.gracePeriodActive,
        gracePeriodRemainingDays: p.gracePeriodRemainingDays ?? null,
      });

      if (complianceRes) {
        setMembers(
          complianceRes.workspaceMemberCompliance as MemberCompliance[]
        );
      }
    } catch (error) {
      if (isBackendUnavailableError(error)) {
        markBackendUnavailable(error);
      }
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, [backendAvailable, graphqlService, workspaceId, canEdit]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const updatePolicy = useCallback(
    async (update: PolicyUpdateInput) => {
      setUpdating(true);
      try {
        const result = await graphqlService.gql({
          query: updateWorkspacePolicyMutation,
          variables: {
            workspaceId,
            input: update,
          },
        });
        const updated = result.updateWorkspacePolicy;
        setPolicy({
          requireTOTP: updated.requireTOTP,
          allowEmailOTP: updated.allowEmailOTP,
          gracePeriodDays: updated.gracePeriodDays,
          enforcedAt: updated.enforcedAt ?? null,
          gracePeriodActive: updated.gracePeriodActive,
          gracePeriodRemainingDays: updated.gracePeriodRemainingDays ?? null,
        });
        notify.success({
          title:
            t['com.streamlain.settings.workspace.security.updated-toast']?.() ??
            'Security settings updated',
        });
        // Refresh compliance after policy change
        if (canEdit) {
          const complianceRes = await graphqlService.gql({
            query: workspaceMemberComplianceQuery,
            variables: { workspaceId },
          });
          setMembers(
            complianceRes.workspaceMemberCompliance as MemberCompliance[]
          );
        }
      } catch (error) {
        if (isBackendUnavailableError(error)) {
          markBackendUnavailable(error);
        }
        notify.error({
          title:
            t['com.streamlain.settings.workspace.security.update-failed']?.() ??
            'Failed to update security settings',
        });
      } finally {
        setUpdating(false);
      }
    },
    [graphqlService, workspaceId, canEdit, t]
  );

  const showOfflineNotice =
    !backendAvailable ||
    (loadError !== null && isBackendUnavailableError(loadError));

  if (loading && !showOfflineNotice) {
    return (
      <div className={styles.loadingState}>
        <Loading size={20} />
        <span>
          {t['com.streamlain.settings.workspace.security.loading']?.() ??
            'Loading security settings...'}
        </span>
      </div>
    );
  }

  if (showOfflineNotice) {
    return (
      <div className={styles.loadingState}>
        <span>{offlineLabel}</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.loadingState}>
        <span>
          {t['com.streamlain.settings.workspace.security.load-error']?.() ??
            'Failed to load security settings.'}
        </span>
      </div>
    );
  }

  const compliantCount = members.filter(m => m.hasTOTP || m.hasEmailOTP).length;

  return (
    <div className={styles.securityPanel}>
      {/* Policy Toggles */}
      <div className={styles.policySection}>
        <div className={styles.policyRow}>
          <div className={styles.policyInfo}>
            <span className={styles.policyLabel}>
              {t[
                'com.streamlain.settings.workspace.security.require-2fa.label'
              ]?.() ?? 'Require Two-Factor Authentication'}
            </span>
            <span className={styles.policyDescription}>
              {t[
                'com.streamlain.settings.workspace.security.require-2fa.description'
              ]?.() ??
                'All workspace members must have 2FA enabled to access this workspace.'}
            </span>
          </div>
          <Switch
            data-testid="security-require-totp-switch"
            checked={policy.requireTOTP}
            disabled={!canEdit || updating}
            onChange={(checked: boolean) =>
              void updatePolicy({ requireTOTP: checked })
            }
          />
        </div>

        {policy.requireTOTP && (
          <div className={styles.policyRow}>
            <div className={styles.policyInfo}>
              <span className={styles.policyLabel}>
                {t[
                  'com.streamlain.settings.workspace.security.allow-email-otp.label'
                ]?.() ?? 'Allow Email-OTP as Alternative'}
              </span>
              <span className={styles.policyDescription}>
                {t[
                  'com.streamlain.settings.workspace.security.allow-email-otp.description'
                ]?.() ??
                  'Members without TOTP can use email verification codes instead. Disable to require authenticator app only.'}
              </span>
            </div>
            <Switch
              data-testid="security-allow-email-otp-switch"
              checked={policy.allowEmailOTP}
              disabled={!canEdit || updating}
              onChange={(checked: boolean) =>
                void updatePolicy({ allowEmailOTP: checked })
              }
            />
          </div>
        )}

        {policy.requireTOTP && (
          <div className={styles.policyRow}>
            <div className={styles.policyInfo}>
              <span className={styles.policyLabel}>
                {t[
                  'com.streamlain.settings.workspace.security.grace-period.label'
                ]?.() ?? 'Grace Period (Days)'}
              </span>
              <span className={styles.policyDescription}>
                {t[
                  'com.streamlain.settings.workspace.security.grace-period.description'
                ]?.() ??
                  'Members have this many days to set up 2FA after enforcement is activated.'}
                {policy.gracePeriodActive &&
                  policy.gracePeriodRemainingDays !== null &&
                  ' ' +
                    (t[
                      'com.streamlain.settings.workspace.security.grace-period.remaining'
                    ]?.({
                      count: String(policy.gracePeriodRemainingDays),
                    }) ??
                      `${policy.gracePeriodRemainingDays} day${policy.gracePeriodRemainingDays !== 1 ? 's' : ''} remaining.`)}
              </span>
            </div>
            <input
              data-testid="security-grace-period-input"
              type="number"
              min={1}
              max={30}
              value={policy.gracePeriodDays}
              disabled={!canEdit || updating}
              className={styles.gracePeriodInput}
              onChange={e => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= 30) {
                  setPolicy(prev => ({ ...prev, gracePeriodDays: val }));
                }
              }}
              onBlur={() => {
                void updatePolicy({ gracePeriodDays: policy.gracePeriodDays });
              }}
            />
          </div>
        )}
      </div>

      {/* Compliance Dashboard (Owner/Admin only) */}
      {canEdit && policy.requireTOTP && members.length > 0 && (
        <div
          data-testid="security-compliance-section"
          className={styles.complianceSection}
        >
          <div className={styles.complianceHeader}>
            <span className={styles.complianceTitle}>
              {t[
                'com.streamlain.settings.workspace.security.compliance.title'
              ]?.() ?? 'Member Compliance'}
            </span>
            <span className={styles.complianceStats}>
              {t[
                'com.streamlain.settings.workspace.security.compliance.stats'
              ]?.({
                compliant: String(compliantCount),
                total: String(members.length),
              }) ?? `${compliantCount}/${members.length} compliant`}
            </span>
          </div>

          {members.map(member => {
            const isCompliant =
              member.hasTOTP || (policy.allowEmailOTP && member.hasEmailOTP);
            const method = member.hasTOTP
              ? 'TOTP'
              : member.hasEmailOTP
                ? 'Email-OTP'
                : (t[
                    'com.streamlain.settings.workspace.security.compliance.none'
                  ]?.() ?? 'None');

            return (
              <div
                key={member.userId}
                data-testid={`security-compliance-member-${member.userId}`}
                className={styles.memberRow}
              >
                <div className={styles.memberInfo}>
                  <span className={styles.memberName}>
                    {member.name || member.email}
                  </span>
                  {member.name && (
                    <span className={styles.memberEmail}>{member.email}</span>
                  )}
                </div>
                <span className={styles.roleBadge}>{member.role}</span>
                <span
                  className={
                    isCompliant
                      ? styles.statusCompliant
                      : styles.statusNonCompliant
                  }
                >
                  {isCompliant
                    ? method
                    : (t[
                        'com.streamlain.settings.workspace.security.compliance.not-set-up'
                      ]?.() ?? 'Not set up')}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
